import { useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { getReadingAccessSnapshot, subscribeReadingAccess } from '@/lib/reading-access';
import {
  clearReaderRuntimeSlots, getReaderRuntimeSlots, putReaderRuntimeSlot,
  removeReaderRuntimeSlot, subscribeReaderRuntimeSlots,
} from '@/lib/reader-runtime-slots';

/** A DOM slot only: constructing children never mounts their authoring hooks. */
export function AuthorizedReaderSlot({ id, children }: { id: string; children: ReactNode }) {
  const [target, setTarget] = useState<HTMLDivElement | null>(null);
  const token = useRef(Symbol(id));
  const access = useSyncExternalStore(subscribeReadingAccess, getReadingAccessSnapshot, getReadingAccessSnapshot);
  useLayoutEffect(() => {
    if (!target || access.restricted || !access.ownerId) {
      removeReaderRuntimeSlot(id, token.current);
      return;
    }
    putReaderRuntimeSlot({ id, token: token.current, target, content: children,
      ownerId: access.ownerId, epoch: access.epoch });
  }, [id, target, children, access]);
  useLayoutEffect(() => {
    const current = token.current;
    return () => removeReaderRuntimeSlot(id, current);
  }, [id]);
  return <div ref={setTarget} />;
}

/** Mounted only inside the existing authorized VoiceCaptureProvider. */
export function AuthorizedReaderHost({ ownerId }: { ownerId: string }) {
  const slots = useSyncExternalStore(subscribeReaderRuntimeSlots, getReaderRuntimeSlots, getReaderRuntimeSlots);
  const access = useSyncExternalStore(subscribeReadingAccess, getReadingAccessSnapshot, getReadingAccessSnapshot);
  useLayoutEffect(() => () => clearReaderRuntimeSlots(), [ownerId]);
  if (access.restricted || access.ownerId !== ownerId) return null;
  return <>{slots.map((slot) => slot.ownerId === ownerId && slot.epoch === access.epoch
    ? createPortal(slot.content, slot.target, slot.id) : null)}</>;
}
